import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
// Import useOutletContext if you need appDetails passed from AppDetailPage
// import { useOutletContext } from 'react-router-dom';

// Import the hook to fetch stats
import { useGetAppStats } from '../services/appService';
import { useAuth } from '../context/AuthContext';

// Helper to format stat keys (e.g., articles_count -> Articles)
const formatStatName = (key) => {
  if (!key || !key.includes('_count')) return 'Unknown Stat';
  const name = key.replace('_count', '');
  return name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' ');
}

function DashboardPage() {
  const { appId } = useParams();
  const { isOrgAdmin, currentUser } = useAuth();
  const getStats = useGetAppStats();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadStats = async () => {
      if (!appId) {
        setError('Application ID is missing.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const data = await getStats(appId);
        setStats(data);
      } catch (err) { 
        console.error("Error loading stats:", err);
        setError(err.message || 'Failed to load application statistics.'); 
      } 
      finally { setLoading(false); }
    };
    loadStats();
  }, [appId, getStats]);

  // Map backend keys to display info
  const statDisplayOrder = [
      { key: 'users_count', label: 'Users' },
      { key: 'articles_count', label: 'Articles' },
      { key: 'events_count', label: 'Events' },
      { key: 'map_pois_count', label: 'Map POIs' },
      { key: 'chat_groups_count', label: 'Chat Groups' },
      // Add more keys here if your backend returns them
  ];

  if (isOrgAdmin()) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Willkommen, {currentUser?.username}!
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Organisations-Admin
          </Typography>
          <Typography variant="body1">
            Du kannst über die Seitenleiste Artikel und Events für deine Organisation erstellen und verwalten.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        App Dashboard 
      </Typography>

      {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
          </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {!loading && !error && stats && (
         <Grid container spacing={3} sx={{ mt: 2 }}>
             {statDisplayOrder.map(({ key, label }) => {
                const value = stats[key];
                const isError = value === 'Error'; // Check if backend reported error for this stat
                const displayValue = typeof value === 'number' ? value : 'N/A';

                return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
                        <Paper sx={{ p: 2, textAlign: 'center', border: isError ? '2px solid red' : 'none' }}>
                            <Typography variant="h6">{label}</Typography>
                            {isError ? (
                                <Typography variant="h4" color="error">Error</Typography>
                            ) : (
                                <Typography variant="h4">{displayValue}</Typography>
                            )}
                        </Paper>
                    </Grid>
                );
             })}
             {/* Display any stats returned from backend but not in explicit order */} 
             {Object.keys(stats)
                .filter(key => !statDisplayOrder.some(item => item.key === key))
                .map(key => {
                    const value = stats[key];
                    const isError = value === 'Error';
                    const displayValue = typeof value === 'number' ? value : 'N/A';
                    return (
                         <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
                            <Paper sx={{ p: 2, textAlign: 'center', border: isError ? '2px solid red' : 'none' }}>
                                <Typography variant="h6">{formatStatName(key)}</Typography>
                                {isError ? (
                                    <Typography variant="h4" color="error">Error</Typography>
                                ) : (
                                    <Typography variant="h4">{displayValue}</Typography>
                                )}
                            </Paper>
                        </Grid>
                    )
                })}
        </Grid>
      )}

       {!loading && !error && !stats && (
           <Typography sx={{ mt: 2 }}>Could not load stats data.</Typography>
       )}
    </Box>
  );
}

export default DashboardPage; 